/**
 * Converts a HEX color + alpha to its opaque version (white background).
 * @param hex - HEX color (e.g., "#RRGGBB" or "RRGGBB").
 * @param alpha - Opacity (0 to 1, e.g., 0.2 for 20%).
 * @returns Opaque HEX color (e.g., "#2466cc").
 */
export function hexWithAlphaToOpaque(hex: string, alpha: number): string {
  let cleanHex = hex.replace(/^#/, "");
  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split("")
      .map((c) => c + c)
      .join("");
  }

  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  const calcOpaque = (channel: number) =>
    Math.round((channel - (1 - alpha) * 255) / alpha);

  const newR = calcOpaque(r);
  const newG = calcOpaque(g);
  const newB = calcOpaque(b);

  const clampToHex = (val: number) =>
    Math.max(0, Math.min(255, Math.round(val)))
      .toString(16)
      .padStart(2, "0");

  return `#${clampToHex(newR)}${clampToHex(newG)}${clampToHex(newB)}`;
}

/** Parses three- or six-digit HEX colors into RGB channels. */
export function hexToRgb(
  hex: string
): { red: number; green: number; blue: number } | undefined {
  const cleanHex = hex.trim().replace(/^#/, "");
  const expanded =
    cleanHex.length === 3
      ? cleanHex
          .split("")
          .map((channel) => channel + channel)
          .join("")
      : cleanHex;

  if (!/^[\da-f]{6}$/i.test(expanded)) {
    return undefined;
  }

  return {
    red: parseInt(expanded.slice(0, 2), 16),
    green: parseInt(expanded.slice(2, 4), 16),
    blue: parseInt(expanded.slice(4, 6), 16),
  };
}
